import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
const SUBSCRIPTION_ENDPOINTS = {
  portalSession: `${API_URL}/api/subscription/create-customer-portal-session`,
  operationCompletion: (operationId: string) =>
    `${API_URL}/api/subscription/operations/${encodeURIComponent(operationId)}/completion`,
  operationResumeIntent: (operationId: string) =>
    `${API_URL}/api/subscription/operations/${encodeURIComponent(operationId)}/resume-intent`,
} as const;
export const COUPON_CODE_INVALID_MESSAGE = 'クーポンコードに誤りがあるか、無効です。';

interface CreateSubscriptionIntentApiResponse {
  operation_id?: string;
  operationId?: string;
  client_secret?: string;
  clientSecret?: string;
  subscription_id?: string;
  subscriptionId?: string;
  intent_type?: string;
  intentType?: string;
  payment_intent_status?: string;
  paymentIntentStatus?: string;
  is_payment_pending?: boolean;
  isPaymentPending?: boolean;
  status_message?: string;
  statusMessage?: string;
  latest_invoice_id?: string;
  latestInvoiceId?: string;
  payment_method_types?: string[];
  paymentMethodTypes?: string[];
  payment_method_domain?: PaymentMethodDomainApiPayload;
  paymentMethodDomain?: PaymentMethodDomainApiPayload;
  subscription_status?: string;
  subscriptionStatus?: string;
  latest_invoice_total?: number;
  latestInvoiceTotal?: number;
  latest_invoice_subtotal?: number;
  latestInvoiceSubtotal?: number;
  latest_invoice_tax?: number;
  latest_invoice_discount?: number;
  latestInvoiceDiscount?: number;
  latest_invoice_discount_amount?: number;
  latestInvoiceDiscountAmount?: number;
  latestInvoiceTax?: number;
  latest_invoice_due_date?: number;
  latestInvoiceDueDate?: number;
  hosted_invoice_url?: string;
  hostedInvoiceUrl?: string;
  invoice_pdf_url?: string;
  invoicePdfUrl?: string;
  invoice_pdf?: string;
}

interface CreateSetupIntentApiResponse {
  operation_id?: string;
  operationId?: string;
  client_secret?: string;
  clientSecret?: string;
}

interface SubscriptionPriceApiResponse {
  id: string;
  currency?: string | null;
  unitAmount?: number | null;
  unitAmountDecimal?: string | null;
  nickname?: string | null;
  product?: {
    id?: string | null;
    name?: string | null;
  } | null;
  recurring?: {
    interval?: string | null;
    intervalCount?: number | null;
  } | null;
  type?: string | null;
  active?: boolean | null;
  metadata?: Record<string, string> | null;
}

export interface SubscriptionPrice {
  id: string;
  currency?: string;
  unitAmount?: number;
  unitAmountDecimal?: string;
  nickname?: string;
  product?: {
    id?: string;
    name?: string;
  };
  recurring?: {
    interval?: string;
    intervalCount?: number;
  };
  type?: string;
  active?: boolean;
  metadata?: Record<string, string>;
}

export interface CreateSubscriptionIntentResponse {
  clientSecret?: string;
  operationId: string;
  subscriptionId?: string;
  intentType?: string;
  paymentIntentStatus?: string;
  isPaymentPending?: boolean;
  statusMessage?: string;
  latestInvoiceId?: string;
  subscriptionStatus?: string;
  latestInvoiceTotal?: number;
  latestInvoiceSubtotal?: number;
  latestInvoiceTax?: number;
  latestInvoiceDiscount?: number;
  latestInvoiceDueDate?: number;
  paymentMethodTypes?: string[];
  paymentMethodDomain?: PaymentMethodDomain;
  hostedInvoiceUrl?: string;
  invoicePdfUrl?: string;
  httpStatus: number;
}

interface PaymentMethodDomainApiPayload {
  domain?: string | null;
  status?: string | null;
  verification_status?: string | null;
  verificationStatus?: string | null;
  is_verified?: boolean | null;
  isVerified?: boolean | null;
  error?: string | null;
  livemode?: boolean | null;
}

export interface PaymentMethodDomain {
  domain?: string;
  status?: string;
  verificationStatus?: string;
  isVerified?: boolean;
  error?: string;
  livemode?: boolean;
}


interface SubscriptionDetailApiResponse {
  current_period_end?: unknown;
  currentPeriodEnd?: unknown;
  cancel_at_period_end?: unknown;
  cancelAtPeriodEnd?: unknown;
}

export interface SubscriptionDetail {
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
}

export interface CreateSetupIntentResponse {
  clientSecret: string;
  operationId: string;
}

let setupIntentInFlight: Promise<CreateSetupIntentResponse> | undefined;

export const createSetupIntent = async (): Promise<CreateSetupIntentResponse> => {
  if (setupIntentInFlight) {
    return setupIntentInFlight;
  }

  const request = (async (): Promise<CreateSetupIntentResponse> => {
    const idempotencyKey = createUuidV4();
    let lastError: unknown;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const response = await axios.post<CreateSetupIntentApiResponse>(
          `${API_URL}/api/subscription/create-setup-intent`,
          {},
          {
            headers: { 'Idempotency-Key': idempotencyKey },
            timeout: 10_000,
          },
        );

        const data = response.data || {};
        const clientSecret = data.clientSecret ?? data.client_secret;
        const operationId = data.operationId ?? data.operation_id;
        if (!clientSecret || !operationId) {
          throw new Error('invalid_setup_intent_response');
        }
        return { clientSecret, operationId };
      } catch (error) {
        lastError = error;
        if (attempt === 0 && isRetryableNetworkError(error)) {
          continue;
        }
        break;
      }
    }

    throw new Error(
      subscriptionApiErrorMessage(
        lastError,
        'カード登録の準備に失敗しました。時間をおいて再度お試しください。',
      ),
    );
  })();

  setupIntentInFlight = request;
  try {
    return await request;
  } finally {
    if (setupIntentInFlight === request) {
      setupIntentInFlight = undefined;
    }
  }
};

export interface BillingAddress {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface BillingDetails {
  name?: string;
  email?: string;
  phone?: string;
  address?: BillingAddress;
}

interface CreateSubscriptionIntentOptions {
  promotionCode?: string;
  billingDetails?: BillingDetails;
  userId?: string;
  replaceAtPeriodEnd?: boolean;
  setupIntentId?: string;
}

const createUuidV4 = (): string => {
  const cryptoApi =
    typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return Array.from(bytes)
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')
    .replace(
      /^(........)(....)(....)(....)(............)$/u,
      '$1-$2-$3-$4-$5',
    );
};

interface IdempotencyContext {
  fingerprint: string;
  idempotencyKey: string;
  storageKey: string;
}

const idempotencyContextFor = (
  operation: string,
  userId: string,
  requestPayload: Record<string, unknown>,
): IdempotencyContext => {
  const fingerprint = JSON.stringify(requestPayload);
  const storageKey = ['subscription-operation', userId, operation].join(':');
  try {
    const stored = window.sessionStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored) as {
        fingerprint?: string;
        idempotencyKey?: string;
      };
      if (parsed.fingerprint === fingerprint && parsed.idempotencyKey) {
        return {
          fingerprint,
          idempotencyKey: parsed.idempotencyKey,
          storageKey,
        };
      }
    }
    const idempotencyKey = createUuidV4();
    window.sessionStorage.setItem(
      storageKey,
      JSON.stringify({ fingerprint, idempotencyKey }),
    );
    return { fingerprint, idempotencyKey, storageKey };
  } catch {
    return {
      fingerprint,
      idempotencyKey: createUuidV4(),
      storageKey,
    };
  }
};

const clearIdempotencyContext = (context: IdempotencyContext): void => {
  try {
    const stored = window.sessionStorage.getItem(context.storageKey);
    if (!stored) {
      return;
    }
    const parsed = JSON.parse(stored) as {
      fingerprint?: string;
      idempotencyKey?: string;
    };
    if (
      parsed.fingerprint === context.fingerprint &&
      parsed.idempotencyKey === context.idempotencyKey
    ) {
      window.sessionStorage.removeItem(context.storageKey);
    }
  } catch {
    // Storage failures must not turn a confirmed server operation into a UI error.
  }
};

const subscriptionIntentInFlight = new Map<
  string,
  Promise<CreateSubscriptionIntentResponse>
>();

const normalizeSubscriptionIntentResponse = (
  data: CreateSubscriptionIntentApiResponse,
  status: number,
): CreateSubscriptionIntentResponse => {
  const clientSecret = data.clientSecret ?? data.client_secret;
  const subscriptionId = data.subscriptionId ?? data.subscription_id;
  const operationId = data.operationId ?? data.operation_id;
  const intentType = data.intentType ?? data.intent_type;
  const paymentIntentStatus = data.paymentIntentStatus ?? data.payment_intent_status;
  const statusMessage = data.statusMessage ?? data.status_message;
  const latestInvoiceId = data.latestInvoiceId ?? data.latest_invoice_id;
  const subscriptionStatus = data.subscriptionStatus ?? data.subscription_status;
  const rawIsPaymentPending =
    data.isPaymentPending ?? data.is_payment_pending;
  const latestInvoiceTotal = data.latestInvoiceTotal ?? data.latest_invoice_total;
  const latestInvoiceSubtotal = data.latestInvoiceSubtotal ?? data.latest_invoice_subtotal;
  const latestInvoiceTax = data.latestInvoiceTax ?? data.latest_invoice_tax;
  const latestInvoiceDiscount =
    data.latestInvoiceDiscount ??
    data.latestInvoiceDiscountAmount ??
    data.latest_invoice_discount_amount ??
    data.latest_invoice_discount;
  const latestInvoiceDueDate = data.latestInvoiceDueDate ?? data.latest_invoice_due_date;
  const paymentMethodTypes = data.paymentMethodTypes ?? data.payment_method_types;
  const domainPayload = data.paymentMethodDomain ?? data.payment_method_domain;
  const hostedInvoiceUrl = data.hostedInvoiceUrl ?? data.hosted_invoice_url;
  const invoicePdfUrl = data.invoicePdfUrl ?? data.invoice_pdf_url ?? data.invoice_pdf ?? undefined;

  if (!operationId) {
    throw new Error('Failed to retrieve operation ID for subscription intent');
  }

  const paymentMethodDomain: PaymentMethodDomain | undefined = domainPayload
    ? {
        domain: domainPayload.domain ?? undefined,
        status: domainPayload.status ?? undefined,
        verificationStatus:
          domainPayload.verificationStatus ?? domainPayload.verification_status ?? undefined,
        isVerified:
          typeof domainPayload.isVerified === 'boolean'
            ? domainPayload.isVerified
            : typeof domainPayload.is_verified === 'boolean'
              ? domainPayload.is_verified
              : undefined,
        error: domainPayload.error ?? undefined,
        livemode:
          typeof domainPayload.livemode === 'boolean' ? domainPayload.livemode : undefined,
      }
    : undefined;

  return {
    clientSecret: clientSecret ?? undefined,
    subscriptionId: subscriptionId ?? undefined,
    operationId,
    intentType: intentType ?? undefined,
    paymentIntentStatus: paymentIntentStatus ?? undefined,
    isPaymentPending:
      typeof rawIsPaymentPending === 'boolean' ? rawIsPaymentPending : undefined,
    statusMessage: statusMessage ?? undefined,
    latestInvoiceId: latestInvoiceId ?? undefined,
    subscriptionStatus: subscriptionStatus ?? undefined,
    latestInvoiceTotal: typeof latestInvoiceTotal === 'number' ? latestInvoiceTotal : undefined,
    latestInvoiceSubtotal:
      typeof latestInvoiceSubtotal === 'number' ? latestInvoiceSubtotal : undefined,
    latestInvoiceTax: typeof latestInvoiceTax === 'number' ? latestInvoiceTax : undefined,
    latestInvoiceDueDate:
      typeof latestInvoiceDueDate === 'number' ? latestInvoiceDueDate : undefined,
    latestInvoiceDiscount:
      typeof latestInvoiceDiscount === 'number' ? latestInvoiceDiscount : undefined,
    paymentMethodTypes: Array.isArray(paymentMethodTypes)
      ? paymentMethodTypes
          .filter((type): type is string => typeof type === 'string')
          .map((type) => type.trim().toLowerCase())
          .filter((type) => type.length > 0)
      : undefined,
    paymentMethodDomain,
    hostedInvoiceUrl: typeof hostedInvoiceUrl === 'string' ? hostedInvoiceUrl : undefined,
    invoicePdfUrl: typeof invoicePdfUrl === 'string' ? invoicePdfUrl : undefined,
    httpStatus: status,
  };
};

interface ResumeSubscriptionIntentApiResponse {
  operation_id?: string;
  operationId?: string;
  client_secret?: string | null;
  clientSecret?: string | null;
  intent_type?: string | null;
  intentType?: string | null;
  payment_intent_status?: string | null;
  paymentIntentStatus?: string | null;
  status_message?: string | null;
  statusMessage?: string | null;
}

export interface ResumeSubscriptionIntentResponse {
  operationId: string;
  clientSecret?: string;
  intentType?: string;
  paymentIntentStatus?: string;
  statusMessage?: string;
}

export type SubscriptionCompletionState = 'confirmed' | 'pending' | 'failed';

interface SubscriptionCompletionApiResponse {
  operation_id?: string;
  operationId?: string;
  state?: string;
  status?: string;
  subscription_status?: string | null;
  subscriptionStatus?: string | null;
  payment_intent_status?: string | null;
  paymentIntentStatus?: string | null;
  message_code?: string | null;
  messageCode?: string | null;
  message?: string | null;
  status_message?: string | null;
  statusMessage?: string | null;
}

export interface SubscriptionCompletion {
  operationId: string;
  state: SubscriptionCompletionState;
  subscriptionStatus?: string;
  paymentIntentStatus?: string;
  messageCode?: string;
}

interface CustomerPortalSessionApiResponse {
  url?: string;
}

const SUBSCRIPTION_API_ERROR_MESSAGES: Record<string, string> = {
  operation_not_found:
    '決済処理が見つかりませんでした。サブスクリプション画面から状態をご確認ください。',
  operation_forbidden: 'この決済処理の状態を確認する権限がありません。',
  operation_expired:
    '決済処理の確認期限が切れています。サブスクリプション画面から状態をご確認ください。',
  operation_in_progress: '同じ契約の処理が進行中です。しばらく待ってから状態を確認してください。',
  resume_intent_unavailable:
    '決済の再開情報を取得できませんでした。時間をおいて再度お試しください。',
  portal_not_configured:
    '契約管理画面を現在利用できません。時間をおいて再度お試しください。',
  portal_session_unavailable:
    '契約管理画面を現在利用できません。時間をおいて再度お試しください。',
  customer_not_found:
    '契約情報を確認できませんでした。サポートへお問い合わせください。',
  rate_limited: 'リクエストが集中しています。時間をおいて再度お試しください。',
  subscription_operation_not_found:
    '決済処理が見つかりませんでした。サブスクリプション画面から状態をご確認ください。',
  payment_intent_not_resumable:
    '決済の再開情報を取得できませんでした。サブスクリプション画面から状態をご確認ください。',
  stripe_customer_not_found:
    '契約情報を確認できませんでした。サポートへお問い合わせください。',
  stripe_portal_url_unavailable:
    '契約管理画面を現在利用できません。時間をおいて再度お試しください。',
  stripe_service_unavailable:
    '決済サービスへ接続できませんでした。時間をおいて再度お試しください。',
  stripe_ownership_conflict:
    '契約情報の所有関係を確認できませんでした。サポートへお問い合わせください。',
  setup_intent_not_succeeded:
    'カード登録が完了していません。カード情報を確認してから再度お試しください。',
  idempotency_key_reused:
    '申込み内容が変更されています。画面を再読み込みしてから再度お試しください。',
  current_period_end_unavailable:
    '現在の契約終了日を確認できません。時間をおいて再度お試しください。',
  promotion_code_invalid: COUPON_CODE_INVALID_MESSAGE,
  promotion_code_not_found: COUPON_CODE_INVALID_MESSAGE,
  promotion_code_ineligible: COUPON_CODE_INVALID_MESSAGE,
  invalid_promotion_code: COUPON_CODE_INVALID_MESSAGE,
  coupon_code_invalid: COUPON_CODE_INVALID_MESSAGE,
  coupon_invalid: COUPON_CODE_INVALID_MESSAGE,
  coupon_not_found: COUPON_CODE_INVALID_MESSAGE,
  vip_subscription_forbidden:
    'このアカウントでは契約手続きを行えません。',
  subscription_not_found:
    '契約情報を確認できませんでした。サポートへお問い合わせください。',
  idempotency_key_required:
    '決済リクエストを安全に処理できませんでした。画面を再読み込みして再度お試しください。',
  invalid_idempotency_key:
    '決済リクエストを安全に処理できませんでした。画面を再読み込みして再度お試しください。',
  stripe_customer_missing:
    '契約情報を確認できませんでした。サポートへお問い合わせください。',
  stripe_customer_deleted:
    '契約情報を確認できませんでした。サポートへお問い合わせください。',
  stripe_subscription_missing:
    '契約情報を確認できませんでした。サポートへお問い合わせください。',
  stripe_current_subscription_conflict:
    '現在の契約状態と申込み内容が一致しません。サポートへお問い合わせください。',
  stripe_operation_id_required:
    '決済処理を特定できませんでした。サブスクリプション画面から再度お試しください。',
  active_subscription_replace_at_period_end_required:
    '現在の契約があるため、契約終了時の切替としてお申し込みください。',
  current_subscription_not_reconcilable:
    '現在の契約状態を確認できませんでした。サポートへお問い合わせください。',
  setup_intent_required_for_replacement:
    '契約切替にはカード登録が必要です。カード情報を確認して再度お試しください。',
  setup_intent_customer_mismatch:
    'カード登録と契約情報を確認できませんでした。サポートへお問い合わせください。',
  stripe_payment_method_domain_not_configured:
    'カード決済を現在利用できません。時間をおいて再度お試しください。',
  stripe_payment_method_domain_not_ready:
    'カード決済を現在利用できません。時間をおいて再度お試しください。',
  stripe_subscription_state_unavailable:
    '契約状態を確認できませんでした。時間をおいて再度お試しください。',
  stripe_invoice_preview_unavailable:
    '金額を事前確認できませんでした。合計はStripe確認画面で確定します。',
  stripe_checkout_url_unavailable:
    '決済画面を開けませんでした。時間をおいて再度お試しください。',
  failed_to_schedule_old_subscription_cancellation:
    '契約切替の予約を完了できませんでした。契約状態をご確認ください。',
};

const stableSubscriptionApiErrorCode = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') {
    return detail.trim().toLowerCase();
  }
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const code = detail.code;
    if (typeof code === 'string') {
      return code.trim().toLowerCase();
    }
  }
  const apiError = error.response?.data?.error;
  return typeof apiError === 'string' ? apiError.trim().toLowerCase() : undefined;
};

const subscriptionApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const code = stableSubscriptionApiErrorCode(error);
  return (code && SUBSCRIPTION_API_ERROR_MESSAGES[code]) || fallback;
};

const normalizeCompletionState = (
  value: unknown,
): SubscriptionCompletionState => {
  if (typeof value !== 'string') {
    throw new Error('invalid_completion_state');
  }
  const normalized = value.trim().toLowerCase();
  if (
    normalized !== 'confirmed' &&
    normalized !== 'pending' &&
    normalized !== 'failed'
  ) {
    throw new Error('invalid_completion_state');
  }
  return normalized;
};

export const resumeSubscriptionIntent = async (
  operationId: string,
): Promise<ResumeSubscriptionIntentResponse> => {
  try {
    const response = await axios.post<ResumeSubscriptionIntentApiResponse>(
      SUBSCRIPTION_ENDPOINTS.operationResumeIntent(operationId),
      {},
      { headers: { 'Cache-Control': 'no-store' } },
    );
    const data = response.data || {};
    const resolvedOperationId = data.operationId ?? data.operation_id ?? operationId;
    const clientSecret = data.clientSecret ?? data.client_secret ?? undefined;
    const intentType = data.intentType ?? data.intent_type ?? undefined;
    const paymentIntentStatus =
      data.paymentIntentStatus ?? data.payment_intent_status ?? undefined;
    const statusMessage = data.statusMessage ?? data.status_message ?? undefined;
    if (resolvedOperationId !== operationId) {
      throw new Error('operation_id_mismatch');
    }

    return {
      operationId: resolvedOperationId,
      ...(clientSecret ? { clientSecret } : {}),
      ...(intentType ? { intentType } : {}),
      ...(paymentIntentStatus ? { paymentIntentStatus } : {}),
      ...(statusMessage ? { statusMessage } : {}),
    };
  } catch (error) {
    throw new Error(
      subscriptionApiErrorMessage(
        error,
        '決済の再開に必要な情報を取得できませんでした。時間をおいて再度お試しください。',
      ),
    );
  }
};

export const fetchSubscriptionCompletion = async (
  operationId: string,
): Promise<SubscriptionCompletion> => {
  try {
    const response = await axios.get<SubscriptionCompletionApiResponse>(
      SUBSCRIPTION_ENDPOINTS.operationCompletion(operationId),
      { headers: { 'Cache-Control': 'no-store' } },
    );
    const data = response.data || {};
    const resolvedOperationId = data.operationId ?? data.operation_id ?? operationId;
    const subscriptionStatus =
      data.subscriptionStatus ?? data.subscription_status ?? undefined;
    const paymentIntentStatus =
      data.paymentIntentStatus ?? data.payment_intent_status ?? undefined;
    const state = normalizeCompletionState(data.state);
    const rawMessageCode =
      data.messageCode ??
      data.message_code ??
      data.message ??
      data.statusMessage ??
      data.status_message ??
      undefined;
    const messageCode =
      typeof rawMessageCode === 'string' && rawMessageCode.trim()
        ? rawMessageCode.trim().toLowerCase()
        : undefined;
    if (resolvedOperationId !== operationId) {
      throw new Error('operation_id_mismatch');
    }

    return {
      operationId: resolvedOperationId,
      state,
      ...(subscriptionStatus ? { subscriptionStatus } : {}),
      ...(paymentIntentStatus ? { paymentIntentStatus } : {}),
      ...(messageCode ? { messageCode } : {}),
    };
  } catch (error) {
    throw new Error(
      subscriptionApiErrorMessage(
        error,
        '契約状態を確認できませんでした。時間をおいて再度お試しください。',
      ),
    );
  }
};

const isRetryableNetworkError = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  (!error.response || error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT');

const isStripeBillingPortalUrl = (portalUrl: URL): boolean => {
  if (
    portalUrl.origin !== 'https://billing.stripe.com' ||
    portalUrl.username !== '' ||
    portalUrl.password !== '' ||
    portalUrl.hash !== ''
  ) {
    return false;
  }

  const legacyPortalSession =
    /^\/p\/session\/[A-Za-z0-9_-]+$/u.test(portalUrl.pathname) &&
    portalUrl.search === '';
  const queryPortalSession =
    portalUrl.pathname === '/p/session' &&
    /^\?secret=[A-Za-z0-9_-]+$/u.test(portalUrl.search);

  return legacyPortalSession || queryPortalSession;
};

export const createCustomerPortalSession = async (): Promise<string> => {
  const idempotencyKey = createUuidV4();
  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await axios.post<CustomerPortalSessionApiResponse>(
        SUBSCRIPTION_ENDPOINTS.portalSession,
        {},
        {
          headers: { 'Idempotency-Key': idempotencyKey },
          timeout: 10_000,
        },
      );
      const rawUrl = response.data?.url;
      if (typeof rawUrl !== 'string') {
        throw new Error('invalid_portal_url');
      }
      const portalUrl = new URL(rawUrl);
      if (!isStripeBillingPortalUrl(portalUrl)) {
        throw new Error('invalid_portal_url');
      }
      return portalUrl.toString();
    } catch (error) {
      lastError = error;
      if (attempt === 0 && isRetryableNetworkError(error)) {
        continue;
      }
      break;
    }
  }
  throw new Error(
    subscriptionApiErrorMessage(
      lastError,
      '契約管理画面を開けませんでした。時間をおいて再度お試しください。',
    ),
  );
};


interface SubscriptionPreviewApiResponse {
  price_id?: string;
  priceId?: string;
  subtotal?: number;
  tax?: number;
  total?: number;
  expected_invoice_due_date?: number;
  discount?: number;
  discount_amount?: number;
  discountAmount?: number;
  expectedInvoiceDueDate?: number;
}

export interface SubscriptionPreviewSummary {
  priceId: string;
  subtotal?: number;
  tax?: number;
  total: number;
  dueDate?: number;
  discount?: number;
}

export const createSubscriptionIntent = async (
  priceId: string,
  options?: CreateSubscriptionIntentOptions,
): Promise<CreateSubscriptionIntentResponse> => {
  const requestPayload = {
    priceId,
    ...(options?.promotionCode ? { promotionCode: options.promotionCode } : {}),
    ...(options?.billingDetails ? { billingDetails: options.billingDetails } : {}),
    ...(options?.replaceAtPeriodEnd ? { replaceAtPeriodEnd: true } : {}),
    ...(options?.setupIntentId ? { setupIntentId: options.setupIntentId } : {}),
  };
  const userId = options?.userId ?? 'unknown-user';
  const context = idempotencyContextFor('card', userId, requestPayload);
  const inFlightKey = `${context.storageKey}:${context.fingerprint}`;
  const existing = subscriptionIntentInFlight.get(inFlightKey);
  if (existing) {
    return existing;
  }

  const request = (async (): Promise<CreateSubscriptionIntentResponse> => {
    try {
      const response = await axios.post<CreateSubscriptionIntentApiResponse>(
        `${API_URL}/api/subscription/create-subscription-intent`,
        requestPayload,
        { headers: { 'Idempotency-Key': context.idempotencyKey } },
      );
      const normalized = normalizeSubscriptionIntentResponse(
        response.data || {},
        response.status,
      );
      clearIdempotencyContext(context);
      return normalized;
    } catch (error) {
      throw new Error(
        subscriptionApiErrorMessage(
          error,
          'カード決済の準備に失敗しました。時間をおいて再度お試しください。',
        ),
      );
    }
  })();

  subscriptionIntentInFlight.set(inFlightKey, request);
  try {
    return await request;
  } finally {
    if (subscriptionIntentInFlight.get(inFlightKey) === request) {
      subscriptionIntentInFlight.delete(inFlightKey);
    }
  }
};

export const createBankTransferInvoice = async (
  priceId: string,
  options?: CreateSubscriptionIntentOptions,
): Promise<CreateSubscriptionIntentResponse> => {
  const requestPayload = {
    priceId,
    ...(options?.promotionCode ? { promotionCode: options.promotionCode } : {}),
    ...(options?.billingDetails ? { billingDetails: options.billingDetails } : {}),
    ...(options?.replaceAtPeriodEnd ? { replaceAtPeriodEnd: true } : {}),
  };
  const userId = options?.userId ?? 'unknown-user';
  const context = idempotencyContextFor('bank-transfer', userId, requestPayload);
  const inFlightKey = `${context.storageKey}:${context.fingerprint}`;
  const existing = subscriptionIntentInFlight.get(inFlightKey);
  if (existing) {
    return existing;
  }

  const request = (async (): Promise<CreateSubscriptionIntentResponse> => {
    try {
      const response = await axios.post<CreateSubscriptionIntentApiResponse>(
        `${API_URL}/api/subscription/create-bank-transfer-invoice`,
        requestPayload,
        { headers: { 'Idempotency-Key': context.idempotencyKey } },
      );
      const normalized = normalizeSubscriptionIntentResponse(
        response.data || {},
        response.status,
      );
      clearIdempotencyContext(context);
      return normalized;
    } catch (error) {
      throw new Error(
        subscriptionApiErrorMessage(
          error,
          '銀行振込の準備に失敗しました。時間をおいて再度お試しください。',
        ),
      );
    }
  })();

  subscriptionIntentInFlight.set(inFlightKey, request);
  try {
    return await request;
  } finally {
    if (subscriptionIntentInFlight.get(inFlightKey) === request) {
      subscriptionIntentInFlight.delete(inFlightKey);
    }
  }
};

export const previewSubscriptionSummary = async (
  priceId: string,
  options?: CreateSubscriptionIntentOptions,
): Promise<SubscriptionPreviewSummary> => {
  try {
    const response = await axios.post<SubscriptionPreviewApiResponse>(
      `${API_URL}/api/subscription/preview-subscription`,
      options?.promotionCode ? { priceId, promotionCode: options.promotionCode } : { priceId },
    );

    const data = response.data || {};
    const resolvedPriceId = data.priceId ?? data.price_id ?? priceId;
    const subtotal = typeof data.subtotal === 'number' ? data.subtotal : undefined;
    const tax = typeof data.tax === 'number' ? data.tax : undefined;
    if (typeof data.total !== 'number') {
      throw new Error('invalid_preview_total');
    }
    const total = data.total;
    const discountValue = data.discountAmount ?? data.discount_amount ?? data.discount;
    const discount = typeof discountValue === 'number' ? discountValue : undefined;
    const dueDate =
      typeof data.expectedInvoiceDueDate === 'number'
        ? data.expectedInvoiceDueDate
        : typeof data.expected_invoice_due_date === 'number'
          ? data.expected_invoice_due_date
          : undefined;

    return {
      priceId: resolvedPriceId,
      ...(subtotal !== undefined ? { subtotal } : {}),
      ...(tax !== undefined ? { tax } : {}),
      total,
      ...(dueDate !== undefined ? { dueDate } : {}),
      ...(discount !== undefined ? { discount } : {}),
    };
  } catch (error) {
    throw new Error(
      subscriptionApiErrorMessage(
        error,
        '金額を事前確認できませんでした。合計はStripe確認画面で確定します。',
      ),
    );
  }
};


export const fetchSubscriptionPrices = async (): Promise<SubscriptionPrice[]> => {
  try {
    const response = await axios.get<{ prices?: SubscriptionPriceApiResponse[] }>(
      `${API_URL}/api/subscription/prices`,
    );

    const prices = response.data?.prices ?? [];

    return prices.map((price) => ({
      id: price.id,
      currency: price.currency ?? undefined,
      unitAmount: typeof price.unitAmount === 'number' ? price.unitAmount : undefined,
      unitAmountDecimal: price.unitAmountDecimal ?? undefined,
      nickname: price.nickname ?? undefined,
      product: price.product
        ? {
            id: price.product.id ?? undefined,
            name: price.product.name ?? undefined,
          }
        : undefined,
      recurring: price.recurring
        ? {
            interval: price.recurring.interval ?? undefined,
            intervalCount: price.recurring.intervalCount ?? undefined,
          }
        : undefined,
      type: price.type ?? undefined,
      active: typeof price.active === 'boolean' ? price.active : undefined,
      metadata: price.metadata ?? undefined,
    }));
  } catch (error) {
    throw new Error(
      subscriptionApiErrorMessage(
        error,
        '価格情報の取得に失敗しました。時間をおいて再度お試しください。',
      ),
    );
  }
};

const normalizeSubscriptionPeriodEnd = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return undefined;
    }

    const numericValue = Number(trimmed);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return numericValue;
    }

    const parsedDate = Date.parse(trimmed);
    if (Number.isFinite(parsedDate) && parsedDate > 0) {
      return parsedDate;
    }
  }

  return undefined;
};

const normalizeSubscriptionDetail = (
  data: SubscriptionDetailApiResponse | null | undefined,
): SubscriptionDetail => {
  const periodEnd =
    data?.currentPeriodEnd ?? data?.current_period_end ?? undefined;
  const normalizedEnd = normalizeSubscriptionPeriodEnd(periodEnd);
  const cancelAtPeriodEnd =
    data?.cancelAtPeriodEnd ?? data?.cancel_at_period_end ?? undefined;
  const normalizedCancellation =
    typeof cancelAtPeriodEnd === 'boolean' ? cancelAtPeriodEnd : undefined;

  return {
    currentPeriodEnd: normalizedEnd,
    cancelAtPeriodEnd: normalizedCancellation,
  };
};

export const fetchSubscriptionDetail = async (): Promise<SubscriptionDetail> => {
  try {
    const response = await axios.get<SubscriptionDetailApiResponse>(`${API_URL}/api/subscription/detail`);

    return normalizeSubscriptionDetail(response.data);
  } catch (error) {
    throw new Error(
      subscriptionApiErrorMessage(
        error,
        'サブスクリプション詳細の取得に失敗しました。時間をおいて再度お試しください。',
      ),
    );
  }
};
